import { memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Marker } from "react-mapbox-gl";

import { COLLECTIONS_PLAN_TYPE } from "../../consts/planTypes";

import { Postcode } from "../../utils/postcode";

import {
  getMatrixProfileType,
  heatmapViewSelector,
  selectedOverlaySelector,
  visibleMultiPostcodeFeaturesSelector,
  multiRoutesPopupLocationSelector,
} from "../../reducers/matrixProfile/selectors";
import { setMultiRoutesPopupLocation } from "../../reducers/matrixProfile";
import { setZoomFromMouseEvent } from "../../reducers/map/thunkActions";
import { zoomLevelSelector } from "../../reducers/map/selectors";

import { View } from "../View";
import { CollectionOverridesMarker } from "./RouteLabelLayer/CollectionOverridesMarker";
import { MultiRoutePopup } from "../MultiRoutePopup/MultiRoutePopup";
import { RouteMarker } from "./RouteLabelLayer/RouteMarker";
import { VolumeMarker } from "./RouteLabelLayer/VolumeMarker";

const useStyles = makeStyles(() => ({
  marker: {
    paddingBottom: "8px",
    zIndex: "4!important",
    "& *": {
      // Markers not selectable
      "user-select": "none" /* supported by Chrome and Opera */,
      "-webkit-user-select": "none" /* Safari */,
      "-khtml-user-select": "none" /* Konqueror HTML */,
      "-moz-user-select": "none" /* Firefox */,
      "-ms-user-select": "none" /* Internet Explorer/Edge */,
    },
  },
  markerContainer: {
    display: "flex",
    alignItems: "flex-end",
    pointer: "cursor",
  },
}));

export const MultiLabelLayer = memo(() => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const multiPostcodeFeatures = useSelector(
    visibleMultiPostcodeFeaturesSelector
  );
  const multiRoutesPopupLocation = useSelector(
    multiRoutesPopupLocationSelector
  );
  const isHeatmapOn = useSelector(heatmapViewSelector);
  const isVolumeOverlayOn = useSelector(selectedOverlaySelector);
  const planType = useSelector(getMatrixProfileType);
  const zoomLevel = useSelector(zoomLevelSelector);

  if (!Array.isArray(multiPostcodeFeatures)) return null;

  const popupVisible = () =>
    isVolumeOverlayOn &&
    multiRoutesPopupLocation?.postcode &&
    Postcode.getLevel(multiRoutesPopupLocation.postcode) === zoomLevel;

  const handleClick = (feature) => {
    if (multiRoutesPopupLocation?.postcode !== feature.postcode)
      dispatch(
        setMultiRoutesPopupLocation({
          coordinates: feature.center,
          postcode: feature.postcode,
        })
      );
    else dispatch(setMultiRoutesPopupLocation(null));
  };

  return (
    <>
      {multiPostcodeFeatures.map((feature, index) => {
        return (
          <Marker
            coordinates={feature.center}
            anchor={"bottom"}
            key={index}
            className={styles.marker}
            onClick={() => handleClick(feature)}
            onWheel={(e) => dispatch(setZoomFromMouseEvent(e))}
            style={!isVolumeOverlayOn ? { pointerEvents: "none" } : {}}
          >
            <View className={styles.markerContainer}>
              <Grid
                item
                container
                alignItems={
                  planType === COLLECTIONS_PLAN_TYPE ? "flex-end" : "flex-start"
                }
                direction={
                  planType === COLLECTIONS_PLAN_TYPE ? "row-reverse" : "row"
                }
              >
                {feature.routes && (
                  <RouteMarker
                    routes={feature.routes}
                    leftMargin={planType === COLLECTIONS_PLAN_TYPE}
                  />
                )}
                {isHeatmapOn && <VolumeMarker postcode={feature.postcode} />}
                {planType === COLLECTIONS_PLAN_TYPE && (
                  <CollectionOverridesMarker
                    collection={feature.collection}
                    mismatches={feature.mismatches}
                  />
                )}
              </Grid>
            </View>
          </Marker>
        );
      })}
      {popupVisible() && <MultiRoutePopup />}
    </>
  );
});

export default MultiLabelLayer;
